import { format } from 'date-fns';
import React from 'react';
import { Loader } from 'react-feather';

import { Order } from '../../types/orderTypes';

interface OrderListTableProps {
  orders: Order[];
  isLoading: boolean;
  movedToDetail: (orderUuid: string) => void;
}

const OrderListTable: React.FC<OrderListTableProps> = ({
  orders,
  isLoading,
  movedToDetail,
}) => {
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'yyyy. MM. dd. HH:mm');
    } catch {
      return dateString;
    }
  };

  const getTotalQuantity = (orderDetails: Order['orderDetails']) => {
    return orderDetails.reduce((total, detail) => total + detail.quantity, 0);
  };

  const hasOrderMemo = (orderMemos: Order['orderMemos']) => {
    return orderMemos && orderMemos.length > 0;
  };

  const getPaymentStatus = (status: Order['status']) => {
    const statusMap = {
      PENDING: 'Pending',
      CANCELLED: 'Cancelled',
      PAID: 'Paid',
    };
    return statusMap[status] || '';
  };

  if (isLoading) {
    return (
      <div className="w-full overflow-auto border border-t-0 border-[#DEDEDE] bg-[#F1F1F1]">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="w-[220px] whitespace-nowrap border-b border-[#DEDEDE] bg-[#EFEFEF] px-4 py-2 text-left text-sm font-medium text-[#707070]">
                Order Number
              </th>
              <th className="w-[140px] whitespace-nowrap border-b border-[#DEDEDE] bg-[#EFEFEF] px-4 py-2 text-left text-sm font-medium text-[#707070]">
                Date
              </th>
              <th className="w-[76px] whitespace-nowrap border-b border-[#DEDEDE] bg-[#EFEFEF] px-4 py-2 text-left text-sm font-medium text-[#707070]">
                Memo
              </th>
              <th className="w-[180px] whitespace-nowrap border-b border-[#DEDEDE] bg-[#EFEFEF] px-4 py-2 text-left text-sm font-medium text-[#707070]">
                Payment
              </th>
              <th className="w-[220px] whitespace-nowrap border-b border-[#DEDEDE] bg-[#EFEFEF] px-4 py-2 text-left text-sm font-medium text-[#707070]">
                Email
              </th>
              <th className="w-[140px] whitespace-nowrap border-b border-[#DEDEDE] bg-[#EFEFEF] px-4 py-2 text-left text-sm font-medium text-[#707070]">
                Delivery
              </th>
              <th className="w-[140px] whitespace-nowrap border-b border-[#DEDEDE] bg-[#EFEFEF] px-4 py-2 text-left text-sm font-medium text-[#707070]">
                Quantity
              </th>
              <th className="w-[200px] whitespace-nowrap border-b border-[#DEDEDE] bg-[#EFEFEF] px-4 py-2 text-left text-sm font-medium text-[#707070]">
                Orderer Name
              </th>
            </tr>
          </thead>
        </table>

        <div className="flex h-64 w-full items-center justify-center bg-white">
          <Loader className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full overflow-auto border border-t-0 border-[#DEDEDE] bg-[#F1F1F1]">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th className="w-[220px] whitespace-nowrap border-b border-[#DEDEDE] bg-[#EFEFEF] px-4 py-2 text-left text-sm font-medium text-[#707070]">
              Order Number
            </th>
            <th className="w-[140px] whitespace-nowrap border-b border-[#DEDEDE] bg-[#EFEFEF] px-4 py-2 text-left text-sm font-medium text-[#707070]">
              Date
            </th>
            <th className="w-[76px] whitespace-nowrap border-b border-[#DEDEDE] bg-[#EFEFEF] px-4 py-2 text-left text-sm font-medium text-[#707070]">
              Memo
            </th>
            <th className="w-[180px] whitespace-nowrap border-b border-[#DEDEDE] bg-[#EFEFEF] px-4 py-2 text-left text-sm font-medium text-[#707070]">
              Payment
            </th>
            <th className="w-[220px] whitespace-nowrap border-b border-[#DEDEDE] bg-[#EFEFEF] px-4 py-2 text-left text-sm font-medium text-[#707070]">
              Email
            </th>
            <th className="w-[140px] whitespace-nowrap border-b border-[#DEDEDE] bg-[#EFEFEF] px-4 py-2 text-left text-sm font-medium text-[#707070]">
              Delivery
            </th>
            <th className="w-[140px] whitespace-nowrap border-b border-[#DEDEDE] bg-[#EFEFEF] px-4 py-2 text-left text-sm font-medium text-[#707070]">
              Quantity
            </th>
            <th className="w-[200px] whitespace-nowrap border-b border-[#DEDEDE] bg-[#EFEFEF] px-4 py-2 text-left text-sm font-medium text-[#707070]">
              Orderer Name
            </th>
          </tr>
        </thead>
      </table>

      <div className="min-h-[calc(100vh-56px)] w-full overflow-y-auto">
        <table className="min-w-full table-fixed border-collapse">
          <tbody className="bg-white">
            {orders.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-4 py-8 text-center text-sm text-gray-500"
                >
                  No orders found.
                </td>
              </tr>
            ) : (
              orders.map((order) => (
                <tr key={order.uuid}>
                  <td
                    className="w-[220px] cursor-pointer whitespace-nowrap border-b border-[#DEDEDE] px-4 py-2.5 text-left text-sm font-medium text-[#1D1D1D] hover:underline"
                    role="button"
                    onClick={() => movedToDetail(order.uuid)}
                  >
                    {order.orderNumber}
                  </td>
                  <td className="w-[140px] whitespace-nowrap border-b border-[#DEDEDE] px-4 py-2.5 text-left text-sm text-[#1D1D1D]">
                    {formatDate(order.createdAt)}
                  </td>
                  <td className="w-[76px] whitespace-nowrap border-b border-[#DEDEDE] px-4 py-2.5 text-left text-sm text-[#1D1D1D]">
                    <div className="flex justify-center">
                      {hasOrderMemo(order.orderMemos) && (
                        <div className="h-2 w-2 rounded-full bg-[#51D148]" />
                      )}
                    </div>
                  </td>
                  <td className="w-[180px] whitespace-nowrap border-b border-[#DEDEDE] px-4 py-2.5 text-left text-sm text-[#1D1D1D]">
                    {getPaymentStatus(order.status)}
                  </td>
                  <td className="w-[220px] whitespace-nowrap border-b border-[#DEDEDE] px-4 py-2.5 text-left text-sm text-[#1D1D1D]">
                    {order.user.email}
                  </td>
                  <td className="w-[140px] whitespace-nowrap border-b border-[#DEDEDE] px-4 py-2.5 text-left text-sm text-[#1D1D1D]">
                    {order.deliveryMethod}
                  </td>
                  <td className="w-[140px] whitespace-nowrap border-b border-[#DEDEDE] px-4 py-2.5 text-left text-sm text-[#1D1D1D]">
                    {getTotalQuantity(order.orderDetails)}
                  </td>
                  <td className="w-[200px] whitespace-nowrap border-b border-[#DEDEDE] px-4 py-2.5 text-left text-sm text-[#1D1D1D]">
                    {order.user.representativeName}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OrderListTable;
